import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  ArrowDownTrayIcon,
  BeakerIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import type { LatestReportSummary } from '../../types/diagnostics';
import { DiagnosticReportSheet } from './diagnostics/DiagnosticReportSheet';
import { ReportChip } from './diagnostics/ReportChip';
import { SymptomForm } from './diagnostics/SymptomForm';
import { diagnosticsListKey, useStartDiagnosis } from './diagnostics/useDiagnosticReport';
import { isReportInFlight, readLastSymptom, rememberSymptom, REPORT_POLL_MS, statusChip } from './diagnostics/reportLogic';

/**
 * Diagnostics bundles pulled off a Scout — logcat, crash records, the
 * WebView's console ring, and the storage/track/GPS state that explains
 * them.
 *
 * Before this existed, a boat reporting "it keeps crashing" gave support
 * nothing to look at. The list leads with the facts that answer the common
 * questions without downloading anything: which build the boat is actually
 * on, whether processes died from a CRASH or a LOW_MEMORY kill, and whether
 * localStorage was sitting against its quota.
 *
 * Each bundle can be handed to Claude ("Diagnose"), which reads it against
 * what the skipper reported and writes a report: a message for the skipper,
 * a fix link for a Claude Code session, and the evidence. The open report
 * lives in the URL (`?report=drp_…`) so the notification email can link to it.
 */

interface DiagnosticsSummary {
  launcher?: string | null;
  vortexnav?: string | null;
  webview?: string | null;
  android?: string | null;
  crash_count?: number | null;
  crash_kinds?: string[];
  exit_reasons?: string[];
  localstorage_bytes?: number | null;
  localstorage_at_quota?: boolean | null;
  local_tracks?: number | null;
  recording_in_flight?: boolean | null;
  track_count?: number | null;
  track_points?: number | null;
  nmea_db_mb?: number | null;
  gps_satellites?: number | null;
  console_lines?: number | null;
  collection_errors?: string[];
}

interface Bundle {
  id: string;
  reason: string;
  collected_at: string | null;
  uploaded_at: string;
  bytes: number;
  schema_version: number;
  summary: DiagnosticsSummary | null;
  /** Newest Diagnose report for this bundle; absent from older APIs. */
  latest_report?: LatestReportSummary | null;
}

const REASON_STYLES: Record<string, string> = {
  crash: 'bg-red-900/30 text-red-300 border border-red-700/50',
  command: 'bg-blue-900/30 text-blue-300 border border-blue-700/50',
  manual: 'bg-slate-700/50 text-slate-300 border border-slate-600/50',
};

const LIST_POLL_MS = 15000;

const PRIMARY_BUTTON =
  'inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-3 text-[15px] font-medium text-white transition-colors hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50';
const SECONDARY_BUTTON =
  'inline-flex items-center gap-2 rounded-lg bg-slate-700 px-4 py-3 text-[15px] text-white transition-colors hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-50';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function versionsOf(s: DiagnosticsSummary): string {
  return [s.launcher ? `PolarisOS ${s.launcher}` : null, s.vortexnav ? `VN ${s.vortexnav}` : null]
    .filter(Boolean)
    .join(' · ');
}

function reportLine(r: LatestReportSummary): string {
  if (r.headline) return r.headline;
  if (isReportInFlight(r.status)) return 'Claude is reading the bundle…';
  if (r.status === 'failed') return 'See why, and retry';
  return 'Open the report';
}

function Fact({ label, value, alert }: { label: string; value: React.ReactNode; alert?: boolean }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex flex-col">
      <span className="text-[13px] uppercase tracking-wider text-slate-500">{label}</span>
      <span className={`text-[15px] ${alert ? 'text-red-300 font-semibold' : 'text-slate-200'}`}>{value}</span>
    </div>
  );
}

function DeviceDiagnosticsTab({ deviceId }: { deviceId: string }) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const openReportId = searchParams.get('report');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [collectOpen, setCollectOpen] = useState(false);
  const [collectedWithSymptom, setCollectedWithSymptom] = useState(false);
  const [diagnosing, setDiagnosing] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ bundleId: string; message: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: diagnosticsListKey(deviceId),
    queryFn: () => api.adminListDeviceDiagnostics(deviceId),
    // Faster while a row is still being diagnosed, so its chip turns over promptly.
    refetchInterval: (q) => {
      const rows: Bundle[] = (q.state.data as { data?: Bundle[] } | undefined)?.data ?? [];
      return rows.some((r) => isReportInFlight(r.latest_report?.status)) ? REPORT_POLL_MS : LIST_POLL_MS;
    },
  });

  const bundles: Bundle[] = data?.data ?? [];

  const collectMutation = useMutation({
    mutationFn: (symptom: string | undefined) => api.adminRequestDeviceDiagnostics(deviceId, symptom),
    onSuccess: (_data, symptom) => {
      setCollectedWithSymptom(!!symptom);
      setCollectOpen(false);
      queryClient.invalidateQueries({ queryKey: diagnosticsListKey(deviceId) });
    },
  });

  const startDiagnosis = useStartDiagnosis(deviceId);

  const openReport = (reportId: string, replace = false) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', 'diagnostics');
        next.set('report', reportId);
        return next;
      },
      // Opening from a row adds a history entry, so a phone's Back closes it.
      { replace },
    );
  };

  const closeReport = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('report');
        return next;
      },
      { replace: true },
    );
  };

  const diagnose = (bundleId: string, symptom: string | undefined) => {
    setRowError(null);
    startDiagnosis.mutate(
      { bundleId, symptom },
      {
        onSuccess: (result) => {
          if ('reportId' in result) {
            setDiagnosing(null);
            openReport(result.reportId);
          } else {
            setRowError({ bundleId, message: result.error.message });
          }
        },
        onError: () => setRowError({ bundleId, message: 'Could not start the diagnosis. Please try again.' }),
      },
    );
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">Diagnostics</h3>
            <p className="mt-1 text-[14px] text-slate-400">
              Logs, crash records and app state collected from this Scout. A request is queued
              until the boat next has a link — an offline vessel collects when it reconnects.
            </p>
          </div>
          {!collectOpen && (
            <button
              onClick={() => {
                collectMutation.reset();
                setCollectOpen(true);
              }}
              className={SECONDARY_BUTTON}
            >
              <BeakerIcon className="h-5 w-5" />
              Collect now
            </button>
          )}
        </div>
        {collectOpen && (
          <div className="mt-4 rounded-lg border border-slate-700 bg-slate-900/40 p-4">
            <SymptomForm
              initialValue={readLastSymptom(deviceId)}
              helper="If you describe the problem, Claude will diagnose the bundle automatically when it arrives."
              submitLabel="Request bundle"
              pendingLabel="Requesting…"
              pending={collectMutation.isPending}
              onSubmit={(symptom) => {
                rememberSymptom(deviceId, symptom);
                collectMutation.mutate(symptom);
              }}
              onCancel={() => setCollectOpen(false)}
            />
          </div>
        )}
        {collectMutation.isError && (
          <p className="mt-3 text-[14px] text-red-400">Could not request diagnostics. Please try again.</p>
        )}
        {collectMutation.isSuccess && (
          <p className="mt-3 text-[14px] text-green-400">
            Requested. The Scout picks this up on its next check and uploads a bundle.
            {collectedWithSymptom && ' Claude will diagnose it automatically when it arrives.'}
          </p>
        )}
      </div>

      <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-5">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider mb-4">Bundles</h3>

        {isLoading && <p className="text-[15px] text-slate-400">Loading…</p>}
        {!isLoading && bundles.length === 0 && (
          <p className="text-[15px] text-slate-400">
            No diagnostics have been collected from this Scout yet.
          </p>
        )}

        <div className="space-y-3">
          {bundles.map((b) => {
            const s = b.summary ?? {};
            const badExit = (s.exit_reasons ?? []).some((r) => r === 'CRASH' || r === 'LOW_MEMORY' || r === 'ANR');
            const open = expanded === b.id;
            const versions = versionsOf(s);
            const latest = b.latest_report ?? null;
            const chip = statusChip(latest);
            const formOpen = diagnosing === b.id;
            return (
              <div key={b.id} data-testid={`bundle-row-${b.id}`} className="min-w-0 rounded-lg border border-slate-700 bg-slate-900/40">
                <button
                  onClick={() => setExpanded(open ? null : b.id)}
                  aria-expanded={open}
                  className="w-full text-left p-4 flex flex-wrap items-center gap-x-4 gap-y-2"
                >
                  <span className={`text-[13px] px-2 py-1 rounded ${REASON_STYLES[b.reason] ?? REASON_STYLES.manual}`}>
                    {b.reason}
                  </span>
                  <span className="text-[15px] text-slate-200">
                    {formatDistanceToNow(new Date(b.uploaded_at), { addSuffix: true })}
                  </span>
                  <span className="text-[14px] text-slate-400">{formatBytes(b.bytes)}</span>
                  {(s.crash_count ?? 0) > 0 && (
                    <span className="flex items-center gap-1 text-[14px] text-red-300">
                      <ExclamationTriangleIcon className="h-4 w-4" />
                      {s.crash_count} crash{s.crash_count === 1 ? '' : 'es'}
                    </span>
                  )}
                  {badExit && (
                    <span className="text-[14px] text-amber-300">{(s.exit_reasons ?? []).join(', ')}</span>
                  )}
                  {s.localstorage_at_quota && (
                    <span className="text-[14px] text-amber-300">storage at quota</span>
                  )}
                  {/* Own line on a phone; right-aligned where there is room. */}
                  {versions && (
                    <span className="basis-full break-words text-[14px] text-slate-400 sm:ml-auto sm:basis-auto">{versions}</span>
                  )}
                </button>

                {latest && chip && (
                  <button
                    onClick={() => openReport(latest.id)}
                    className="flex w-full items-start gap-3 border-t border-slate-700/70 px-4 py-3 text-left transition-colors hover:bg-slate-800/60"
                  >
                    {/* Chip above the headline on a phone, beside it where there is room. */}
                    <span className="flex min-w-0 flex-1 flex-col items-start gap-1.5 sm:flex-row sm:gap-3">
                      <ReportChip chip={chip} />
                      <span className="min-w-0 break-words text-[15px] text-slate-200">{reportLine(latest)}</span>
                    </span>
                    <ChevronRightIcon className="mt-1 h-5 w-5 shrink-0 text-slate-500" aria-hidden="true" />
                  </button>
                )}

                <div className="flex flex-wrap gap-2 border-t border-slate-700/70 px-4 py-3">
                  {!formOpen && (
                    <button
                      onClick={() => {
                        setRowError(null);
                        setDiagnosing(b.id);
                      }}
                      className={PRIMARY_BUTTON}
                    >
                      <SparklesIcon className="h-5 w-5" aria-hidden="true" />
                      Diagnose
                    </button>
                  )}
                  <a href={api.deviceDiagnosticsDownloadUrl(deviceId, b.id)} className={SECONDARY_BUTTON}>
                    <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
                    Download
                  </a>
                </div>

                {formOpen && (
                  <div className="border-t border-slate-700/70 p-4">
                    <SymptomForm
                      initialValue={readLastSymptom(deviceId)}
                      helper="Claude reads the bundle and writes a report — usually under a minute."
                      submitLabel="Start diagnosis"
                      pendingLabel="Starting…"
                      pending={startDiagnosis.isPending}
                      onSubmit={(symptom) => diagnose(b.id, symptom)}
                      onCancel={() => setDiagnosing(null)}
                    />
                  </div>
                )}

                {rowError?.bundleId === b.id && (
                  <p role="alert" className="border-t border-slate-700/70 px-4 py-3 text-[15px] text-amber-300">
                    {rowError.message}
                  </p>
                )}

                {open && (
                  <div className="border-t border-slate-700 p-4 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Fact label="PolarisOS" value={s.launcher} />
                      <Fact label="VortexNav" value={s.vortexnav} />
                      <Fact label="WebView" value={s.webview} />
                      <Fact label="Android" value={s.android} />
                      <Fact label="Crashes" value={s.crash_count} alert={(s.crash_count ?? 0) > 0} />
                      <Fact label="Exit reasons" value={(s.exit_reasons ?? []).join(', ') || null} alert={badExit} />
                      <Fact label="Crash kinds" value={(s.crash_kinds ?? []).join(', ') || null} />
                      <Fact label="Console lines" value={s.console_lines} />
                      <Fact
                        label="localStorage"
                        value={s.localstorage_bytes ? `${formatBytes(s.localstorage_bytes)}${s.localstorage_at_quota ? ' (at quota)' : ''}` : null}
                        alert={!!s.localstorage_at_quota}
                      />
                      <Fact label="Local tracks" value={s.local_tracks} />
                      <Fact label="Recording in flight" value={s.recording_in_flight === null || s.recording_in_flight === undefined ? null : String(s.recording_in_flight)} />
                      <Fact label="Tracks on Scout" value={s.track_count} />
                      <Fact label="Track points" value={s.track_points} />
                      <Fact label="NMEA DB" value={s.nmea_db_mb != null ? `${s.nmea_db_mb} MB` : null} />
                      <Fact label="GPS satellites" value={s.gps_satellites} alert={s.gps_satellites === 0} />
                      <Fact label="Collected" value={b.collected_at ? new Date(b.collected_at).toLocaleString() : null} />
                    </div>

                    {(s.collection_errors ?? []).length > 0 && (
                      <p className="text-[14px] text-amber-300">
                        Sections that failed to collect: {(s.collection_errors ?? []).join(', ')}
                      </p>
                    )}

                    <p className="text-[13px] text-slate-500">
                      Bundles are redacted on the Scout: key names and sizes, never values, and
                      positions rounded to about a kilometre. The download is a .json.gz file.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <DiagnosticReportSheet
        deviceId={deviceId}
        reportId={openReportId}
        onClose={closeReport}
        onOpenReport={(id) => openReport(id, true)}
      />
    </div>
  );
}

export default DeviceDiagnosticsTab;
